import Request from "utils/requests/api";
import initializeConfig from "utils/requests/api.config";
import { ISaleChannel } from "./lobby-model";

export const BASE_URL_GATES_EVENTS = async () => {
  const { gates } = await initializeConfig();
  return `${gates}gate-events`;
};

export const BASE_URL_TRUCKS = async () => {
  const { gates } = await initializeConfig();
  return `${gates}trucks`;
};

export const BASE_URL_MANAGEMENT = async () => {
  const { management } = await initializeConfig();
  return `${management}`;
};

export const BASE_URL_YARD_REGISTERS = async () => {
  const { gates } = await initializeConfig();
  return `${gates}yard-registers/`;
};

export const BASE_URL_GATES = async () => {
  const { gates } = await initializeConfig();
  return `${gates}`;
};

export interface IRequestYardRegister {
  yardRegisterId: number;
}

export interface IRequestCheckinYardRegister {
  vehiclePlate: string;
}

export interface IRequestVehicleProfile {
  plate: string;
}

export interface IRequestYardRegisterActive {
  locationId: number;
  _size: number;
  _page: number;
  filter?: string;
}

export interface IRequestInboundUpdate {
  gateEventId: number;
}

export interface IDrivers {
  name: string;
  cpf: string;
}

export interface IRequestPostInbound {
  carrier: string;
  vehiclePlate: string;
  trailersPlates: string[];
  observation: string;
  eventTime: string;
  user?: string;
  locationId: number;
  processId: number;
  processDescription: string;
  documents: IRequestPostDocument[];
  yardRegisterId?: number;
  fromCheckin?: boolean;
  drivers?: IDrivers[];
  saleChannel?: ISaleChannel;
  trailerType?: number;
}

export interface IRequestPutInbound {
  carrier: string;
  vehiclePlate: string;
  trailersPlates: string[];
  observation: string;
  eventTime: string;
  user?: string;
  processId: number;
  processDescription: string;
  documents: IRequestPostDocument[];
  drivers?: IDrivers[];
  trailerType?: number;
}

export interface IRequestPostDocument {
  id: number;
  type: string;
  value: string;
}

export interface IRequestPostOutbound {
  trailersPlates: string[];
  observation: string;
  yardRegisterId: string | number;
  eventTime: string;
  user?: string;
  outboundProcessDescription: string;
  outboundProcessId: number;
}

export interface IRequestGetDocuments {
  processCorrelationId: string;
  eventType: number;
}

export interface IRequestVehicleForDock {
  id: number;
  dock: string;
}

export interface IRequestEntryPermission {
  driverId: string;
  vehicleId: string;
  saleChannelType: number;
}

export const getYarnRegister = async ({ yardRegisterId }: IRequestYardRegister) => {
  const baseUrlGates = await BASE_URL_GATES_EVENTS();
  return (await Request).get(`${baseUrlGates}:inbound`, { yardRegisterId });
};

export const saveInbound = async (params: IRequestPostInbound) => {
  const baseUrlTrucks = await BASE_URL_TRUCKS();
  return (await Request).post(`${baseUrlTrucks}:inbound`, params);
};

export const saveOutbound = async (params: IRequestPostOutbound) => {
  const baseUrlTrucks = await BASE_URL_TRUCKS();
  return (await Request).post(`${baseUrlTrucks}:outbound`, params);
};

export const updateInbound = async (params: IRequestPutInbound, gateEventId: IRequestInboundUpdate) => {
  const baseUrlTrucks = await BASE_URL_TRUCKS();
  return (await Request).put(`${baseUrlTrucks}:inbound/${gateEventId}`, params);
};

export const getProcesses = async ({ locationId, eventType }) => {
  const baseUrlManagement = await BASE_URL_MANAGEMENT();
  return (await Request).get(`${baseUrlManagement}processes/search/by-location`, { locationId, eventType });
};

export const getSaleChannels = async () => {
  const baseUrlManagement = await BASE_URL_MANAGEMENT();
  return (await Request).get(`${baseUrlManagement}sale-channels`);
};

export const getDocuments = async ({ processCorrelationId, eventType }: IRequestGetDocuments) => {
  const baseUrlManagement = await BASE_URL_MANAGEMENT();
  return (await Request).get(`${baseUrlManagement}processes/${processCorrelationId}/documents`, { eventType });
};

export const getInboundsActives = async ({ locationId, _size, _page, filter }: IRequestYardRegisterActive) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).get(`${baseUrlGatesEvents}/active-vehicles:paginated`, { locationId, _size, _page, filter });
};

export const getInboundsCheckins = async ({ locationId, _size, _page, filter }: IRequestYardRegisterActive) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).get(`${baseUrlGatesEvents}/checkins:paginated`, { locationId, _size, _page, filter });
};

export const getCheckinYardRegister = async ({ yardRegisterId }: IRequestYardRegister) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).get(`${baseUrlGatesEvents}:checkin`, { yardRegisterId });
};

export const saveRequestVehicleForGate = async (id) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).post(`${baseUrlGatesEvents}/${id}:request-vehicle-for-gate`);
};

export const saveRequestVehicleForDock = async ({ id, dock }) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).post(`${baseUrlGatesEvents}/${id}:request-dock-entry`, { dock });
};

export const getCheckinYardRegisterByVehiclePlate = async ({ vehiclePlate }: IRequestCheckinYardRegister) => {
  const baseUrlGatesEvents = await BASE_URL_GATES_EVENTS();
  return (await Request).get(`${baseUrlGatesEvents}:checkin`, { vehiclePlate });
};

export const getVehicleProfileByPlate = async ({ plate }: IRequestVehicleProfile) => {
  const baseUrlGates = await BASE_URL_GATES();
  return (await Request).get(`${baseUrlGates}vehicle-profiles/${encodeURIComponent(plate)}`);
};

export const getEventsHistory = async (yardRegisterId: number) => {
  const baseUrlYardRegisters = await BASE_URL_YARD_REGISTERS();
  return (await Request).get(`${baseUrlYardRegisters}${yardRegisterId}/event-history`);
};

export const finishVehicleRegister = async (yardRegisterId) => {
  const baseUrlTrucks = await BASE_URL_TRUCKS();
  return (await Request).delete(`${baseUrlTrucks}/${yardRegisterId}`);
};

export const getEntryPermission = async ({ driverId, vehicleId, saleChannelType }: IRequestEntryPermission) => {
  const baseUrlGates = await BASE_URL_GATES();
  return (await Request).get(`${baseUrlGates}documents/entry-permission:info`, { driverId, vehicleId, saleChannelType });
};
